<template>
    <v-container fluid class="pa-6">
        <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
            <div>
                <h2 class="text-h6">Panoramica preventivi</h2>
                <div class="text-body-2 text-medium-emphasis">Importi al netto di IVA, per data del preventivo</div>
            </div>
            <filtro-periodo />
        </div>

        <!-- KPI -->
        <v-row>
            <v-col v-for="k in schede" :key="k.titolo" cols="12" sm="6" md="3">
                <v-card variant="outlined" :loading="caricamentoKpi" class="h-100">
                    <v-card-text>
                        <div class="text-body-2 text-medium-emphasis">{{ k.titolo }}</div>
                        <div class="text-h5 font-weight-bold my-1">{{ k.valore }}</div>
                        <div v-if="k.delta !== null" class="d-flex align-center">
                            <v-chip size="x-small" variant="flat" :color="k.delta >= 0 ? 'success' : 'error'"
                                :prepend-icon="k.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'">
                                {{ formatPerc(Math.abs(k.delta)) }}
                            </v-chip>
                            <span class="text-caption text-medium-emphasis ml-2">vs anno precedente</span>
                        </div>
                        <div v-else-if="k.nota" class="text-caption text-medium-emphasis">{{ k.nota }}</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Andamento -->
        <v-card variant="outlined" class="mt-4" :loading="caricamentoAndamento">
            <v-card-title class="d-flex align-center">
                Andamento nel tempo
                <v-spacer />
                <v-btn-toggle v-model="raggruppa" mandatory density="compact" variant="outlined" divided
                    @update:model-value="caricaAndamento">
                    <v-btn value="anno">Anno</v-btn>
                    <v-btn value="mese">Mese</v-btn>
                </v-btn-toggle>
            </v-card-title>
            <v-card-text>
                <grafico-barra-linea v-if="andamento.length" :labels="etichetteAndamento"
                    :valori="andamento.map(p => p.valore)" :conteggi="andamento.map(p => p.n)"
                    etichetta-valori="Valore preventivi" />
                <div v-else-if="!caricamentoAndamento" class="text-medium-emphasis">Nessun preventivo nel periodo.</div>
            </v-card-text>
        </v-card>

        <v-row class="mt-0">
            <v-col cols="12" md="6">
                <v-card variant="outlined" :loading="caricamentoAndamento" class="h-100">
                    <v-card-title>Valore medio per preventivo</v-card-title>
                    <v-card-text>
                        <grafico-barra-linea v-if="andamento.length" :labels="etichetteAndamento"
                            :valori="andamento.map(p => p.valoreMedio)" tipo-valori="line"
                            etichetta-valori="Valore medio" :altezza="260" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card variant="outlined" :loading="caricamentoStagionalita" class="h-100">
                    <v-card-title>Stagionalità</v-card-title>
                    <v-card-subtitle>Somma dei preventivi per mese dell'anno, su tutto il periodo</v-card-subtitle>
                    <v-card-text>
                        <grafico-barra-linea v-if="stagionalita.length" :labels="stagionalita.map(s => nomeMese(s.mese))"
                            :valori="stagionalita.map(s => s.valore)" :conteggi="stagionalita.map(s => s.n)"
                            etichetta-valori="Valore preventivi" :altezza="260" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import Functions from "@/utils/functions.js";
import periodo from "@/mixins/periodo.js";
import FiltroPeriodo from "@/components/statistiche/FiltroPeriodo.vue";
import GraficoBarraLinea from "@/components/statistiche/GraficoBarraLinea.vue";

export default {
    name: "HomePage",
    components: { FiltroPeriodo, GraficoBarraLinea },
    mixins: [periodo],

    data() {
        return {
            kpi: null,
            // Solo senza filtro sul periodo: KPI dell'anno corrente (da inizio anno).
            kpiAnnoCorrente: null,
            andamento: [],
            stagionalita: [],
            raggruppa: "anno",
            caricamentoKpi: false,
            caricamentoAndamento: false,
            caricamentoStagionalita: false,
        };
    },

    computed: {
        etichetteAndamento() {
            return this.andamento.map((p) =>
                p.mese ? Functions.nomeMese(p.mese) + " " + p.anno : String(p.anno)
            );
        },

        schede() {
            const c = this.kpi ? this.kpi.corrente : null;
            const p = this.kpi ? this.kpi.precedente : null;
            if (!c) return [];

            const delta = (corrente, precedente, prec = p) =>
                prec && precedente > 0 ? ((corrente - precedente) / precedente) * 100 : null;

            // Su tutto lo storico il totale complessivo non è significativo (gli
            // importi hanno senso per anno): si mostra il valore dell'anno corrente.
            const ac = this.kpiAnnoCorrente;
            const schedaValore = ac
                ? {
                    titolo: "Valore " + new Date().getFullYear() + " (da inizio anno)",
                    valore: Functions.formatEuro(ac.corrente.valoreTotale),
                    delta: delta(ac.corrente.valoreTotale, ac.precedente && ac.precedente.valoreTotale, ac.precedente),
                }
                : {
                    titolo: "Valore totale",
                    valore: Functions.formatEuro(c.valoreTotale),
                    delta: delta(c.valoreTotale, p && p.valoreTotale),
                };

            return [
                {
                    titolo: "Preventivi",
                    valore: Functions.formatNumero(c.numPreventivi),
                    delta: delta(c.numPreventivi, p && p.numPreventivi),
                },
                schedaValore,
                {
                    titolo: "Valore medio",
                    valore: Functions.formatEuro(c.valoreMedio),
                    delta: delta(c.valoreMedio, p && p.valoreMedio),
                    nota: "Mediana " + Functions.formatEuro(c.valoreMediano),
                },
                {
                    titolo: "Clienti distinti",
                    valore: Functions.formatNumero(c.clientiDistinti),
                    delta: delta(c.clientiDistinti, p && p.clientiDistinti),
                },
            ];
        },
    },

    methods: {
        nomeMese: Functions.nomeMese,
        formatPerc: Functions.formatPerc,

        // Dal 1° gennaio a oggi: l'API lo confronta con lo stesso periodo dell'anno prima.
        periodoAnnoCorrente() {
            const oggi = new Date();
            return {
                dal: Functions.dataIso(new Date(oggi.getFullYear(), 0, 1)),
                al: Functions.dataIso(oggi),
            };
        },

        carica() {
            this.caricaKpi();
            this.caricaAndamento();
            this.caricaStagionalita();
        },

        async caricaKpi() {
            this.caricamentoKpi = true;
            const periodoRichiesto = this.periodo;
            const senzaFiltro = !periodoRichiesto.dal && !periodoRichiesto.al;
            try {
                const [dati, annoCorrente] = await Promise.all([
                    this.$store.dispatch("statistiche/kpi"),
                    senzaFiltro ? this.$store.dispatch("statistiche/kpi", this.periodoAnnoCorrente()) : null,
                ]);
                if (periodoRichiesto === this.periodo) {
                    this.kpi = dati;
                    this.kpiAnnoCorrente = annoCorrente;
                }
            } catch (error) {
                console.log(error);
            }
            this.caricamentoKpi = false;
        },

        async caricaAndamento() {
            this.caricamentoAndamento = true;
            const periodoRichiesto = this.periodo;
            const raggruppaRichiesto = this.raggruppa;
            try {
                const dati = await this.$store.dispatch("statistiche/andamento", raggruppaRichiesto);
                if (periodoRichiesto === this.periodo && raggruppaRichiesto === this.raggruppa) this.andamento = dati;
            } catch (error) {
                console.log(error);
            }
            this.caricamentoAndamento = false;
        },

        async caricaStagionalita() {
            this.caricamentoStagionalita = true;
            const periodoRichiesto = this.periodo;
            try {
                const dati = await this.$store.dispatch("statistiche/stagionalita");
                if (periodoRichiesto === this.periodo) this.stagionalita = dati;
            } catch (error) {
                console.log(error);
            }
            this.caricamentoStagionalita = false;
        },
    },
};
</script>
